import {
  useGetAdminInterviewDetail,
  useRequeueInterviewEvaluation,
} from "@/api/generated/hooks/admin/admin";
import type { AdminInterviewDetail } from "@/api/generated/types";
import { PageHeader } from "@/components/features/common/page-header";
import { ContentContainer } from "@/components/features/layout/content-container";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getStoredAuthToken } from "@/lib/auth-token";
import { REPORT_DOWNLOAD_ERROR_MESSAGES, downloadInterviewReport } from "@/lib/interview-report";
import {
  formatDateTime,
  formatFileSize,
  formatInterviewStatus,
  formatRecommendation,
  formatRelativeScore,
  getInterviewDisplayTitle,
} from "@/lib/interview-ui";
import { getApiBaseUrl } from "@/lib/runtime-config";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { EvaluationRunStatus } from "./_components/evaluation-run-status";
import { RecordingPlayer } from "./_components/recording-player";
import {
  type RoleAssessmentView,
  RoleFitCard,
  type RoleFitEntryView,
  type RoleFitView,
  roleLabels,
} from "./_components/role-fit-card";

// 評価実行中 (EVALUATING) の間だけ詳細を再取得し、完了/失敗で停止する間隔
const EVALUATION_POLL_INTERVAL_MS = 5000;

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
type RawResultJson = NonNullable<AdminInterviewDetail["evaluation"]>["rawResultJson"];
type EvidenceConfidence = "low" | "medium" | "high";

interface AnswerEvidenceView {
  confidence: EvidenceConfidence;
  evidence: string;
  missingEvidence: string | null;
  questionIndex: number | null;
  rationale: string | null;
  rubricDimension: string;
}

interface ReviewRecommendationView {
  decision: string;
  followUpQuestions: string[];
  rationale: string;
}

interface SignalSummaryView {
  count: number;
  maxIntensity: number;
  signalType: string;
}

const disconnectReasonLabels: Record<string, string> = {
  stale_live_auto_completed: "一定時間操作が無かったため自動で終了しました（回答済みの内容で評価）",
  stale_live_no_answer: "回答が無いまま一定時間経過したため中断しました",
};

const REQUEUE_DISABLED_STATUSES: readonly string[] = ["SCHEDULED", "READY"];

const rubricLabels: Record<string, string> = {
  communication: "コミュニケーション力",
  experience: "経験・実績",
  learningMotivation: "学習意欲・成長性",
  logicalThinking: "論理的思考力",
  problemSolving: "課題解決力",
  technicalSkill: "技術力",
};

// 評価の重み順 (technicalSkill 0.30 > problemSolving 0.25 > experience 0.20 > logicalThinking 0.10 > communication 0.08 > learningMotivation 0.07)
const rubricDimensionOrder = [
  "technicalSkill",
  "problemSolving",
  "experience",
  "logicalThinking",
  "communication",
  "learningMotivation",
];

const confidenceLabels: Record<EvidenceConfidence, string> = {
  high: "高",
  low: "低",
  medium: "中",
};

const signalLabels: Record<string, string> = {
  hesitation: "ためらい",
  pace: "話速",
  tone: "声色",
};

const adaptiveQuestionSourceLabels: Record<string, string> = {
  github: "GitHub",
  operator: "運営入力",
  resume: "職務経歴書",
  template_rewrite: "固定質問の調整",
};

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-500">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold text-slate-950">{value}</p>
      </CardContent>
    </Card>
  );
}

function isJsonObject(value: JsonValue | undefined): value is { [key: string]: JsonValue } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readRawResultValue(
  rawResultJson: RawResultJson | null | undefined,
  key: string
): JsonValue | undefined {
  return rawResultJson?.[key] as JsonValue | undefined;
}

function readString(value: JsonValue | undefined) {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function readNumber(value: JsonValue | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readStringArray(value: JsonValue | undefined) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    return typeof item === "string" && item.trim().length > 0 ? [item.trim()] : [];
  });
}

function readRoleAssessment(value: JsonValue | undefined): RoleAssessmentView | null {
  if (!isJsonObject(value)) return null;
  const score = readNumber(value.score);
  const rank = readString(value.rank);
  const comment = readString(value.comment);
  if (
    score === null ||
    !Number.isInteger(score) ||
    score < 0 ||
    score > 100 ||
    !rank ||
    !["S", "A", "B", "C", "D"].includes(rank) ||
    !comment ||
    !Array.isArray(value.evidence) ||
    !value.evidence.every((line) => typeof line === "string")
  )
    return null;
  return { score, rank, comment, evidence: readStringArray(value.evidence) };
}

function readRoleFit(detail: AdminInterviewDetail | null | undefined): RoleFitView | null {
  if (readEvaluationSource(detail) === "heuristic") return null;
  const value = readRawResultValue(detail?.evaluation?.rawResultJson, "roleFit");
  if (!isJsonObject(value) || !Array.isArray(value.entries)) return null;
  const primaryRole = readString(value.primaryRole);
  if (value.primaryRole !== null && (!primaryRole || !Object.hasOwn(roleLabels, primaryRole)))
    return null;
  if (value.primaryRationale !== null && typeof value.primaryRationale !== "string") return null;
  const entries: RoleFitEntryView[] = [];
  for (const item of value.entries) {
    if (!isJsonObject(item)) return null;
    const role = readString(item.role);
    const fitRationale = readString(item.fitRationale);
    if (
      !role ||
      !Object.hasOwn(roleLabels, role) ||
      !fitRationale ||
      entries.some((entry) => entry.role === role)
    )
      return null;
    const resume = readRoleAssessment(item.resume);
    const interview = readRoleAssessment(item.interview);
    if (
      (item.resume !== null && !resume) ||
      (item.interview !== null && !interview) ||
      (!resume && !interview)
    )
      return null;
    entries.push({ role, fitRationale, resume, interview });
  }
  if (primaryRole ? !entries.some((entry) => entry.role === primaryRole) : entries.length > 0)
    return null;
  return { primaryRole, primaryRationale: readString(value.primaryRationale), entries };
}

type JudgeMetaView = {
  durationMs: number | null;
  totalTokenCount: number | null;
  responseModelVersion: string | null;
};

function readJudgeMeta(detail: AdminInterviewDetail | null | undefined): JudgeMetaView | null {
  const value = readRawResultValue(detail?.evaluation?.rawResultJson, "judgeMeta");
  if (!isJsonObject(value)) return null;
  return {
    durationMs: readNumber(value.durationMs),
    totalTokenCount: readNumber(value.totalTokenCount),
    responseModelVersion: readString(value.responseModelVersion),
  };
}

function readEvalPromptVersion(detail: AdminInterviewDetail | null | undefined) {
  return readString(readRawResultValue(detail?.evaluation?.rawResultJson, "evalPromptVersion"));
}

function readPartialTurnCount(detail: AdminInterviewDetail | null | undefined) {
  return readNumber(readRawResultValue(detail?.evaluation?.rawResultJson, "partialTurnCount")) ?? 0;
}

function isEvidenceConfidence(value: string): value is EvidenceConfidence {
  return value === "low" || value === "medium" || value === "high";
}

function readEvidenceItem(
  item: JsonValue,
  rubricDimensionOverride: string | null
): AnswerEvidenceView[] {
  if (!isJsonObject(item)) {
    return [];
  }

  const confidence = readString(item.confidence);
  const evidence = readString(item.evidence);
  const missingEvidence = readString(item.missingEvidence);
  const questionIndex = readNumber(item.questionIndex);
  const rationale = readString(item.rationale);
  const rubricDimension = rubricDimensionOverride ?? readString(item.rubricDimension);
  // 根拠本文 evidence と評価軸 rubricDimension があれば表示する。questionIndex / rationale が
  // 欠けていても黙って捨てず、欠損項目はビュー側で省略する（部分的な根拠も見せる）。
  if (!confidence || !isEvidenceConfidence(confidence) || !evidence || !rubricDimension) {
    return [];
  }

  return [
    {
      confidence,
      evidence,
      missingEvidence,
      questionIndex,
      rationale,
      rubricDimension,
    },
  ];
}

function readEvaluationSource(detail: AdminInterviewDetail | null | undefined) {
  return readString(readRawResultValue(detail?.evaluation?.rawResultJson, "source"));
}

function readRubricEvidence(detail: AdminInterviewDetail | null | undefined): AnswerEvidenceView[] {
  const rawRubricEvidence = readRawResultValue(detail?.evaluation?.rawResultJson, "rubricEvidence");
  if (!isJsonObject(rawRubricEvidence)) {
    return [];
  }

  return Object.entries(rawRubricEvidence).flatMap(([rubricDimension, items]) => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.flatMap((item) => readEvidenceItem(item, rubricDimension));
  });
}

function readQualityWarnings(detail: AdminInterviewDetail | null | undefined) {
  return readStringArray(readRawResultValue(detail?.evaluation?.rawResultJson, "qualityWarnings"));
}

function readSafetyNotes(detail: AdminInterviewDetail | null | undefined) {
  return readStringArray(readRawResultValue(detail?.evaluation?.rawResultJson, "safetyNotes"));
}

function readReviewRecommendation(
  detail: AdminInterviewDetail | null | undefined
): ReviewRecommendationView | null {
  const rawRecommendation = readRawResultValue(
    detail?.evaluation?.rawResultJson,
    "reviewRecommendation"
  );
  if (!isJsonObject(rawRecommendation)) {
    return null;
  }

  const decision = readString(rawRecommendation.decision);
  const rationale = readString(rawRecommendation.rationale);
  if (!decision || !rationale) {
    return null;
  }

  return {
    decision,
    followUpQuestions: readStringArray(rawRecommendation.followUpQuestions),
    rationale,
  };
}

function formatSignalOffset(atMs: number) {
  const totalSeconds = Math.max(0, Math.floor(atMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function summarizeSignals(signals: AdminInterviewDetail["signals"]): SignalSummaryView[] {
  const summaries = new Map<string, SignalSummaryView>();
  for (const signal of signals) {
    const current = summaries.get(signal.signalType);
    if (current) {
      current.count += 1;
      current.maxIntensity = Math.max(current.maxIntensity, signal.intensity);
      continue;
    }

    summaries.set(signal.signalType, {
      count: 1,
      maxIntensity: signal.intensity,
      signalType: signal.signalType,
    });
  }

  return [...summaries.values()].sort((left, right) => right.maxIntensity - left.maxIntensity);
}

function normalizeQuestionText(value: string) {
  return value.replace(/\s+/g, "");
}

function wasAdaptiveQuestionAsked(turns: AdminInterviewDetail["turns"], question: string): boolean {
  const normalizedQuestion = normalizeQuestionText(question);
  return turns.some((turn) => {
    const speaker = turn.speaker.toLowerCase();
    if (speaker !== "interviewer" && speaker !== "assistant") {
      return false;
    }

    const transcript = normalizeQuestionText(turn.transcript);
    return transcript === normalizedQuestion || transcript.includes(normalizedQuestion);
  });
}

export function CandidateDetailPage() {
  const { candidateId } = useParams({ from: "/app/candidates/$candidateId" });
  const detailQuery = useGetAdminInterviewDetail(candidateId, {
    query: {
      select: (response) => response.data as AdminInterviewDetail,
      refetchInterval: (query) => {
        const data = query.state.data as { data?: AdminInterviewDetail } | undefined;
        return data?.data?.interview.status === "EVALUATING" ? EVALUATION_POLL_INTERVAL_MS : false;
      },
    },
  });
  const requeueMutation = useRequeueInterviewEvaluation();
  const [resumePending, setResumePending] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [reportPending, setReportPending] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [requeueError, setRequeueError] = useState<string | null>(null);

  const detail = detailQuery.data;
  const partialTurnCount = readPartialTurnCount(detail);
  const rubricEvidence = readRubricEvidence(detail);
  const evaluationSource = readEvaluationSource(detail);
  const roleFit = readRoleFit(detail);
  const judgeMeta = readJudgeMeta(detail);
  const evalPromptVersion = readEvalPromptVersion(detail);
  // 評価根拠は評価軸ごとに、重み順でグルーピングして表示する。
  const rubricEvidenceGroups = rubricDimensionOrder
    .map((dimension) => ({
      dimension,
      items: rubricEvidence.filter((item) => item.rubricDimension === dimension),
    }))
    .filter((group) => group.items.length > 0);
  const qualityWarnings = readQualityWarnings(detail);
  const safetyNotes = readSafetyNotes(detail);
  const reviewRecommendation = readReviewRecommendation(detail);
  const signalSummaries = detail ? summarizeSignals(detail.signals) : [];

  async function handleRequeue() {
    setRequeueError(null);
    try {
      await requeueMutation.mutateAsync({ id: candidateId });
      await detailQuery.refetch();
    } catch {
      setRequeueError("再評価の依頼に失敗しました。時間をおいて再度お試しください。");
    }
  }

  async function handleResumeDownload() {
    const token = getStoredAuthToken();
    if (!token) {
      setResumeError("認証トークンが見つかりません。再度ログインしてください。");
      return;
    }

    setResumeError(null);
    setResumePending(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/interviews/${candidateId}/resume`, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("職務経歴書のダウンロードに失敗しました。");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const fileName = detail?.candidateProfile.resumeFileName ?? `candidate-resume-${candidateId}`;
      anchor.href = url;
      anchor.download = fileName;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setResumeError(
        error instanceof Error ? error.message : "職務経歴書のダウンロードに失敗しました。"
      );
    } finally {
      setResumePending(false);
    }
  }

  async function handleReportDownload() {
    setReportError(null);
    setReportPending(true);
    const result = await downloadInterviewReport({
      candidateName: detail?.interview.candidateName ?? null,
      interviewId: candidateId,
    });
    setReportPending(false);
    if (!result.ok) {
      setReportError(REPORT_DOWNLOAD_ERROR_MESSAGES[result.reason]);
    }
  }

  return (
    <main className="flex-1 overflow-y-auto bg-background">
      <ContentContainer className="space-y-6">
        <PageHeader
          title="面談結果"
          breadcrumbs={[{ label: "候補者管理", href: "/candidates" }, { label: "面談結果" }]}
          subtitle={detail ? (detail.owner?.email ?? detail.interview.ownerUserId) : undefined}
          actions={
            <div className="flex flex-col items-end gap-1">
              <Button
                onClick={() => void handleRequeue()}
                disabled={
                  requeueMutation.isPending ||
                  REQUEUE_DISABLED_STATUSES.includes(detail?.interview.status ?? "")
                }
              >
                再評価する
              </Button>
              {requeueError && (
                <p role="alert" className="text-sm text-rose-600">
                  {requeueError}
                </p>
              )}
            </div>
          }
        />

        {detailQuery.isLoading && <p className="text-sm text-muted-foreground">読み込み中...</p>}
        {detailQuery.isError && (
          <p className="text-sm text-rose-600">面談詳細の取得に失敗しました。</p>
        )}

        {detail && (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <MetricCard
                label="ステータス"
                value={formatInterviewStatus(detail.interview.status)}
              />
              <MetricCard
                label="総合スコア"
                value={formatRelativeScore(detail.evaluation?.overallScore)}
              />
              <MetricCard
                label="判定"
                value={formatRecommendation(detail.evaluation?.recommendation ?? null)}
              />
              <MetricCard
                label="面談日時"
                value={formatDateTime(detail.interview.completedAt ?? detail.interview.startedAt)}
              />
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
              <Card>
                <CardHeader>
                  <CardTitle>候補者とテンプレート</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-slate-700">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Candidate</p>
                    <p className="font-semibold text-slate-900">
                      {detail.candidateProfile.name || detail.owner?.name || "Unknown"}
                    </p>
                    <p>
                      {detail.candidateProfile.email ||
                        detail.owner?.email ||
                        detail.interview.ownerUserId}
                    </p>
                    <div className="mt-3 space-y-2">
                      <p className="text-xs uppercase tracking-wide text-slate-500">GitHub</p>
                      {detail.candidateProfile.githubUrl ? (
                        <a
                          href={detail.candidateProfile.githubUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="break-all text-primary underline-offset-4 hover:underline"
                        >
                          {detail.candidateProfile.githubUrl}
                        </a>
                      ) : (
                        <p>未登録</p>
                      )}
                    </div>
                    <div className="mt-3 space-y-2">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Resume</p>
                      <p>{detail.candidateProfile.resumeFileName ?? "職務経歴書は未登録"}</p>
                      {detail.candidateProfile.hasResume && (
                        <div className="space-y-1 text-xs text-slate-500">
                          <p>種別: {detail.candidateProfile.resumeContentType ?? "未登録"}</p>
                          <p>サイズ: {formatFileSize(detail.candidateProfile.resumeByteSize)}</p>
                          <p>
                            更新日時: {formatDateTime(detail.candidateProfile.resumeUploadedAt)}
                          </p>
                        </div>
                      )}
                      {detail.candidateProfile.hasResume && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => void handleResumeDownload()}
                          disabled={resumePending}
                        >
                          職務経歴書をダウンロード
                        </Button>
                      )}
                      {resumeError && <p className="text-sm text-rose-600">{resumeError}</p>}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Template</p>
                    <p className="font-semibold text-slate-900">
                      {getInterviewDisplayTitle(detail)}
                    </p>
                    <p>{detail.interview.templateSnapshotJson.description}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">導入質問</p>
                    <p>{detail.interview.templateSnapshotJson.introPrompt}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">質問計画</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {detail.interview.templateSnapshotJson.questionPlan.map((question) => (
                        <Badge key={question} variant="outline">
                          {question}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>評価サマリ</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-slate-700">
                  <EvaluationRunStatus interview={detail.interview} />
                  {detail.evaluation ? (
                    <>
                      {partialTurnCount > 0 && (
                        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                          この評価は partial transcript を {partialTurnCount} 件含んでいます。
                        </p>
                      )}
                      {(qualityWarnings.length > 0 || safetyNotes.length > 0) && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                          <p className="font-semibold">評価品質メモ</p>
                          <ul className="mt-2 space-y-1">
                            {qualityWarnings.map((warning) => (
                              <li key={warning}>{warning}</li>
                            ))}
                            {safetyNotes.map((note) => (
                              <li key={note}>安全メモ: {note}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="leading-7">{detail.evaluation.summary}</p>
                      {reviewRecommendation && (
                        <div className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs uppercase tracking-wide text-slate-500">
                              レビュー推奨
                            </p>
                            <Badge variant="outline">{reviewRecommendation.decision}</Badge>
                          </div>
                          <p className="mt-2 leading-7">{reviewRecommendation.rationale}</p>
                          {reviewRecommendation.followUpQuestions.length > 0 && (
                            <ul className="mt-2 space-y-2">
                              {reviewRecommendation.followUpQuestions.map((question) => (
                                <li key={question} className="rounded-lg bg-slate-50 px-3 py-2">
                                  {question}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      <div className="grid gap-4 sm:grid-cols-2">
                        {Object.entries(detail.evaluation.rubricScores).map(([key, value]) => {
                          const item = value as
                            | { score: number; rank: string; comment: string }
                            | number;
                          const isNewFormat =
                            typeof item === "object" && item !== null && "rank" in item;
                          return (
                            <div key={key} className="rounded-lg border border-slate-200 p-3">
                              <p className="text-xs uppercase tracking-wide text-slate-500">
                                {rubricLabels[key] ?? key}
                              </p>
                              {isNewFormat ? (
                                <>
                                  <p className="mt-1 text-lg font-semibold text-slate-900">
                                    {item.rank}
                                    <span className="ml-2 text-sm font-normal text-slate-500">
                                      ({item.score}/100)
                                    </span>
                                  </p>
                                  <p className="mt-2 text-sm leading-6 text-slate-700">
                                    {item.comment}
                                  </p>
                                </>
                              ) : (
                                <p className="mt-1 text-lg font-semibold text-slate-900">
                                  {typeof item === "number" ? item : JSON.stringify(item)}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-500">強み</p>
                          <ul className="mt-2 space-y-2">
                            {detail.evaluation.strengths.map((strength) => (
                              <li key={strength} className="rounded-lg bg-emerald-50 px-3 py-2">
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-500">改善点</p>
                          <ul className="mt-2 space-y-2">
                            {detail.evaluation.improvements.map((improvement) => (
                              <li key={improvement} className="rounded-lg bg-amber-50 px-3 py-2">
                                {improvement}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      {detail.evaluation.reportGeneratedAt && (
                        <div className="space-y-1">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => void handleReportDownload()}
                            disabled={reportPending}
                          >
                            レポートをダウンロード
                          </Button>
                          {reportError && <p className="text-sm text-rose-600">{reportError}</p>}
                        </div>
                      )}
                      {detail.evaluation && (
                        <p
                          className="border-t border-slate-100 pt-3 text-xs text-slate-500"
                          data-testid="evaluation-engine-meta"
                        >
                          {[
                            `評価エンジン: ${judgeMeta?.responseModelVersion ?? detail.evaluation.modelVersion}`,
                            evalPromptVersion ? `プロンプト版: ${evalPromptVersion}` : null,
                            judgeMeta?.durationMs !== null && judgeMeta?.durationMs !== undefined
                              ? `所要 ${Math.round(judgeMeta.durationMs / 1000)} 秒`
                              : null,
                            judgeMeta?.totalTokenCount !== null &&
                            judgeMeta?.totalTokenCount !== undefined
                              ? `${judgeMeta.totalTokenCount.toLocaleString()} tokens`
                              : null,
                          ]
                            .filter((part): part is string => part !== null)
                            .join(" · ")}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {detail.interview.status === "FAILED"
                        ? "文字起こしが不足しているため評価を生成できませんでした。面談データを確認して再評価してください。"
                        : "まだ評価結果は生成されていません。"}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            <RoleFitCard roleFit={roleFit} />

            <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              <Card>
                <CardHeader>
                  <CardTitle>面談設計</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-slate-700">
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      variant={
                        detail.personalization.resumePersonalizationUsed ? "secondary" : "outline"
                      }
                    >
                      {detail.personalization.resumePersonalizationUsed
                        ? "職務経歴書に基づく個別化あり"
                        : "職務経歴書に基づく個別化なし"}
                    </Badge>
                    <Badge variant="outline">
                      候補者別質問: {detail.personalization.adaptiveQuestions.length}件
                    </Badge>
                  </div>

                  {detail.personalization.adaptiveQuestions.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      候補者別質問は記録されていません。
                    </p>
                  )}

                  {detail.personalization.adaptiveQuestions.map((question) => {
                    const asked = wasAdaptiveQuestionAsked(detail.turns, question.question);
                    return (
                      <section key={question.id} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">
                            {adaptiveQuestionSourceLabels[question.source] ?? question.source}
                          </Badge>
                          <Badge variant="secondary">{question.priority}</Badge>
                          <Badge variant={asked ? "secondary" : "outline"}>
                            {asked ? "実施済み" : "未実施"}
                          </Badge>
                          {question.replacesTemplateQuestionIndex !== null && (
                            <span className="text-xs text-slate-500">
                              置換元: Q{question.replacesTemplateQuestionIndex + 1}
                            </span>
                          )}
                        </div>
                        <p className="mt-3 text-xs uppercase tracking-wide text-slate-500">
                          {question.topic}
                        </p>
                        <p className="mt-1 text-sm font-medium leading-7 text-slate-900">
                          {question.question}
                        </p>
                        <p className="mt-2 text-xs leading-6 text-slate-600">
                          {question.rationale}
                        </p>
                        {question.evidenceHint && (
                          <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-6 text-slate-600">
                            根拠: {question.evidenceHint}
                          </p>
                        )}
                      </section>
                    );
                  })}

                  {detail.qualityWarnings.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950">
                      <p className="font-semibold">面談品質メモ</p>
                      <ul className="mt-3 space-y-2">
                        {detail.qualityWarnings.map((warning) => (
                          <li
                            key={`${warning.code}-${warning.sequence ?? "none"}`}
                            className="rounded-lg bg-white/70 px-3 py-2"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline">{warning.code}</Badge>
                              {warning.questionIndex !== null && (
                                <span className="text-xs text-amber-900">
                                  Q{warning.questionIndex + 1}
                                </span>
                              )}
                            </div>
                            <p className="mt-2 leading-7">{warning.message}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>質問ごとの整理</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {detail.questionAnswerSummaries.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      まだ整理結果は生成されていません。再評価してください。
                    </p>
                  )}
                  {detail.questionAnswerSummaries.map((item) => (
                    <section
                      key={`${item.questionIndex}-${item.question}`}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <Badge variant="outline">Q{item.questionIndex + 1}</Badge>
                        {item.questionSource === "template" && (
                          <span className="text-xs text-slate-500">テンプレートから補完</span>
                        )}
                      </div>
                      <div className="mt-4 space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs uppercase tracking-wide text-slate-500">質問</p>
                          <p className="text-sm font-medium leading-7 text-slate-900">
                            {item.question}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs uppercase tracking-wide text-slate-500">
                            候補者の回答
                          </p>
                          <p className="whitespace-pre-line text-sm leading-7 text-slate-700">
                            {item.answer}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs uppercase tracking-wide text-slate-500">AI要約</p>
                          <p className="text-sm leading-7 text-slate-700">
                            {item.answerSummary ?? "要点の要約はまだ生成されていません。"}
                          </p>
                        </div>
                      </div>
                    </section>
                  ))}
                </CardContent>
              </Card>

              {detail.recording && (
                <RecordingPlayer
                  candidateName={detail.candidateProfile.name || detail.owner?.name || null}
                  interviewId={candidateId}
                  contentType={detail.recording.contentType}
                  byteSize={detail.recording.byteSize}
                  durationMs={detail.recording.durationMs}
                  uploadedAt={detail.recording.uploadedAt}
                />
              )}

              <Card>
                <CardHeader>
                  <CardTitle>文字起こし</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {detail.turns.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      文字起こしはまだ保存されていません。
                    </p>
                  )}
                  {detail.turns.map((turn) => (
                    <div key={turn.id} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <Badge variant="outline">{turn.speaker}</Badge>
                        <span className="text-xs text-slate-500">#{turn.sequence}</span>
                      </div>
                      <p className="mt-3 text-sm leading-7 text-slate-700">{turn.transcript}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>評価根拠</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {evaluationSource === "heuristic" && (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      暫定評価です（AI評価エンジン未使用）。評価軸ごとの具体的な根拠を得るには再評価してください。
                    </p>
                  )}
                  {rubricEvidenceGroups.length === 0 && evaluationSource !== "heuristic" && (
                    <p className="text-sm text-muted-foreground">
                      評価軸ごとの根拠は「評価サマリ」の各項目コメントを参照してください。
                    </p>
                  )}
                  {rubricEvidenceGroups.map((group) => (
                    <section
                      key={group.dimension}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        {rubricLabels[group.dimension] ?? group.dimension}
                      </p>
                      <ul className="mt-2 space-y-2">
                        {group.items.map((item) => (
                          <li
                            key={`${item.rubricDimension}-${item.questionIndex ?? "x"}-${item.evidence}`}
                            className="rounded-lg bg-slate-50 px-3 py-2"
                          >
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              {item.questionIndex !== null && (
                                <Badge variant="outline">Q{item.questionIndex + 1}</Badge>
                              )}
                              <span>確信度: {confidenceLabels[item.confidence]}</span>
                            </div>
                            <p className="mt-1 text-sm font-medium leading-7 text-slate-900">
                              {item.evidence}
                            </p>
                            {item.rationale && (
                              <p className="mt-1 text-xs leading-6 text-slate-600">
                                {item.rationale}
                              </p>
                            )}
                            {item.missingEvidence && (
                              <p className="mt-1 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-6 text-amber-900">
                                不足: {item.missingEvidence}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>行動シグナル</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {detail.signals.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      レビュー補助シグナルは記録されていません。
                    </p>
                  )}
                  {signalSummaries.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {signalSummaries.map((summary) => (
                        <Badge key={summary.signalType} variant="secondary">
                          {signalLabels[summary.signalType] ?? summary.signalType}: {summary.count}
                          件 / max {summary.maxIntensity.toFixed(2)}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {detail.signals.map((signal) => {
                    const width = `${Math.min(Math.max(signal.intensity, 0), 1) * 100}%`;
                    return (
                      <div key={signal.id} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <Badge variant="outline">
                            {signalLabels[signal.signalType] ?? signal.signalType}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {formatSignalOffset(signal.atMs)}
                          </span>
                        </div>
                        <div className="mt-3 h-2 rounded-full bg-slate-100">
                          <div className="h-2 rounded-full bg-slate-700" style={{ width }} />
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          intensity: {signal.intensity.toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>セッション状態</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-slate-700">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Live Session</p>
                    <p className="mt-1">
                      {detail.liveSession
                        ? formatInterviewStatus(detail.liveSession.state)
                        : "未参加"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">接続時刻</p>
                    <p className="mt-1">{formatDateTime(detail.liveSession?.connectedAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">最終 heartbeat</p>
                    <p className="mt-1">{formatDateTime(detail.liveSession?.lastHeartbeatAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">切断理由</p>
                    <p className="mt-1">
                      {detail.liveSession?.disconnectReason
                        ? (disconnectReasonLabels[detail.liveSession.disconnectReason] ??
                          detail.liveSession.disconnectReason)
                        : "記録なし"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </ContentContainer>
    </main>
  );
}
